import { D } from "@mobily/ts-belt";
import PropTypes from "prop-types";
import { useEffect } from "react";
import { addons } from "../../../../domain/addon";
import { useCurrentUser } from "../../../../globalState";
import { useAvailableAddonsForProduct } from "../../../../hooks";
import AddonCard, { AddonCardSkeleton } from "../ProductPage/AddonCard";
import {
  categorizeAddons,
  cleanOrphanAddons,
  disabledTextFor,
  shouldDisable,
  toggleAddon,
} from "./addonUtils";
import s from "./AddonSelector.module.css";

const COA_KEY = "coaAddon";

const Section = ({ label, children }) => (
  <div className={s.section}>
    <h3 className={s.sectionLabel}>{label}</h3>
    <div className={s.list}>{children}</div>
  </div>
);

Section.propTypes = {
  label: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
};

AddonSelector.propTypes = {
  productId: PropTypes.number.isRequired,
  selectedAddons: PropTypes.object.isRequired,
  onAddonsChange: PropTypes.func.isRequired,
  coaDismissed: PropTypes.bool,
  onCoaDismiss: PropTypes.func,
  onTrack: PropTypes.func,
};

export default function AddonSelector({
  productId,
  selectedAddons,
  onAddonsChange,
  coaDismissed = false,
  onCoaDismiss,
  onTrack,
}) {
  const { availableAddons, isLoading } =
    useAvailableAddonsForProduct(productId);
  const currentUser = useCurrentUser();

  // Auto-opt-in COA + auto-add VIP for family members.
  // Combined into one effect so both additions apply to the same map —
  // separate effects race on the same render and the second overwrites the first.
  useEffect(() => {
    if (!availableAddons || isLoading) return;

    const coaAddon = availableAddons[COA_KEY];
    const vipAddon = Object.values(availableAddons).find(
      (a) => a.name === addons.VIP_ADDON,
    );

    const autoAddons = [
      !coaDismissed && coaAddon && !selectedAddons[coaAddon.name] && coaAddon,
      currentUser.isStreamilyFamilyMember &&
        vipAddon &&
        !selectedAddons[vipAddon.name] &&
        vipAddon.clone({ addon_price: 0 }),
    ].filter(Boolean);

    if (autoAddons.length > 0) {
      onAddonsChange(
        autoAddons.reduce(
          (acc, addon) => D.set(acc, addon.name, addon),
          selectedAddons,
        ),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isLoading,
    availableAddons,
    currentUser.isStreamilyFamilyMember,
    coaDismissed,
  ]);

  // Clean up addons that are no longer available for the current variant
  useEffect(() => {
    if (!availableAddons || isLoading) return;
    const cleaned = cleanOrphanAddons(selectedAddons, availableAddons);
    if (cleaned) {
      onAddonsChange(cleaned);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableAddons, isLoading]);

  if (isLoading) {
    return (
      <div className={s.root}>
        <Section label="Protect &amp; Present">
          <AddonCardSkeleton />
          <AddonCardSkeleton />
          <AddonCardSkeleton />
        </Section>
        <Section label="Enhance Your Experience">
          <AddonCardSkeleton />
          <AddonCardSkeleton />
        </Section>
      </div>
    );
  }

  if (!availableAddons || Object.keys(availableAddons).length === 0) {
    return null;
  }

  const { physicalAddons, experienceAddons } =
    categorizeAddons(availableAddons);

  const handleToggle = (addon) => {
    const has = Boolean(selectedAddons[addon.name]);
    const next = toggleAddon(selectedAddons, addon);
    onAddonsChange(next);

    if (has && addon.name === addons.COA_ADDON && onCoaDismiss) {
      onCoaDismiss();
    }

    if (onTrack) {
      onTrack(addon, has ? "remove" : "add", productId);
    }
  };

  return (
    <div className={s.root}>
      {physicalAddons.length > 0 && (
        <Section label="Protect &amp; Present">
          {physicalAddons.map((addon) => {
            const has = Boolean(selectedAddons[addon.name]);
            const disabled = shouldDisable(addon, selectedAddons);

            return (
              <AddonCard
                key={addon.name}
                addon={addon}
                isSelected={has}
                isDisabled={disabled}
                onToggle={() => handleToggle(addon)}
                badge={
                  addon.name === addons.COA_ADDON ? "Recommended" : undefined
                }
                disabledText={disabled ? disabledTextFor(addon) : undefined}
              />
            );
          })}
        </Section>
      )}

      {experienceAddons.length > 0 && (
        <Section label="Enhance Your Experience">
          {experienceAddons.map((addon) => {
            const has = Boolean(selectedAddons[addon.name]);
            const isIncluded =
              addon.name === addons.VIP_ADDON &&
              currentUser.isStreamilyFamilyMember;
            const displayAddon = isIncluded
              ? addon.clone({ addon_price: 0 })
              : addon;

            return (
              <AddonCard
                key={addon.name}
                addon={displayAddon}
                isSelected={has}
                isIncluded={isIncluded}
                onToggle={() => handleToggle(addon)}
                badge={isIncluded ? "Included" : undefined}
                includedText={
                  isIncluded ? "Included with your membership" : undefined
                }
              />
            );
          })}
        </Section>
      )}
    </div>
  );
}
